import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    FiGrid, FiHome, FiTool, FiTruck, FiCalendar, FiChevronRight, FiArrowRight,
} from "react-icons/fi";
import { BrickWall, Grid2X2, Truck, Ruler } from "lucide-react";
import { GiWoodBeam } from "react-icons/gi";
import BackButton from "../common/BackButton";

// ─── Primary card (large floating tile) ──────────────────────────────────────
const PrimaryCard = ({ item, onClick, extra }) => {
    const Icon = item.icon;
    return (
        <button
            onClick={onClick}
            className="schedule-primary-card group relative bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 text-left p-6 flex flex-col gap-4 overflow-hidden"
        >
            {/* Left accent bar */}
            <div className={`absolute left-0 top-0 bottom-0 w-[3px] rounded-l-2xl ${item.accent}`} />

            {/* Icon + arrow */}
            <div className="flex items-start justify-between">
                <div className={`w-11 h-11 rounded-xl ${item.iconBg} flex items-center justify-center flex-shrink-0`}>
                    <Icon size={20} className="text-white" />
                </div>
                <FiArrowRight
                    size={15}
                    className="text-gray-300 group-hover:text-gray-500 group-hover:translate-x-0.5 transition-all mt-0.5"
                />
            </div>

            {/* Text */}
            <div className="flex-1">
                <p className="text-[15px] font-bold text-zinc-700 leading-snug mb-1">{item.title}</p>
                <p className="text-[12px] text-gray-400 leading-relaxed">{item.description}</p>
            </div>

            {/* Tags */}
            {item.tags && (
                <div className="flex flex-wrap gap-1.5">
                    {item.tags.map((t, i) => (
                        <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-500 text-[10px] font-medium rounded-md">
                            {t}
                        </span>
                    ))}
                </div>
            )}

            {/* Extra slot (e.g. Finished Deliveries button) */}
            {extra}
        </button>
    );
};

// ─── Dept card (compact square tile) ─────────────────────────────────────────
const DeptCard = ({ item, onClick }) => {
    const Icon = item.icon;
    return (
        <button
            onClick={onClick}
            className="schedule-dept-card group bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 text-left p-4 flex flex-col gap-3"
        >
            <div className="flex items-center justify-between">
                <div className={`w-9 h-9 rounded-lg ${item.iconBg} flex items-center justify-center flex-shrink-0`}>
                    <Icon size={16} className="text-white" />
                </div>
                <FiChevronRight
                    size={13}
                    className="text-gray-200 group-hover:text-gray-400 transition-colors"
                />
            </div>
            <div>
                <p className="text-sm font-bold text-zinc-700 leading-snug">{item.title}</p>
                <p className="text-[11px] text-gray-400 mt-0.5 leading-relaxed">{item.description}</p>
            </div>
        </button>
    );
};

// ─── Section label ────────────────────────────────────────────────────────────
const SectionLabel = ({ children }) => (
    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-3">{children}</p>
);

// ─── Main ─────────────────────────────────────────────────────────────────────
const ScheduleHub = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { companySlug } = useParams();

    const go = (path) =>
        navigate(`/${companySlug}/schedule/${path}`, { state: { from: location.pathname } });

    const primarySchedules = [
        {
            id: "editor",
            title: "Job Editor",
            description: "Comprehensive view of all jobs with advanced filtering and management tools.",
            icon: FiGrid,
            iconBg: "bg-zinc-700",
            accent: "bg-zinc-700",
            path: "editor",
            tags: ["All jobs", "Advanced filters", "Bulk actions"],
        },
        {
            id: "production",
            title: "Production Schedule",
            description: "Track production progress, manage cutting phases and manufacturing.",
            icon: FiTool,
            iconBg: "bg-zinc-700",
            accent: "bg-zinc-700",
            path: "production",
            tags: ["Phase management", "Cutting", "Completion"],
        },
        {
            id: "delivery",
            title: "Delivery Schedule",
            description: "Manage deliveries and assign drivers to completed jobs.",
            icon: FiTruck,
            iconBg: "bg-zinc-700",
            accent: "bg-zinc-700",
            path: "delivery",
            tags: ["Driver assignment", "Tracking", "Status"],
        },
        {
            id: "site-measure",
            title: "Site Measure",
            description: "Schedule on-site measurement visits across all quote types.",
            icon: Ruler,
            iconBg: "bg-zinc-700",
            accent: "bg-zinc-700",
            path: "site-measure",
            tags: ["All types", "Drag & drop", "Live updates"],
        },
    ];

    const deptSchedules = [
        {
            id: "roof",
            title: "Roof",
            description: "Roof design and estimation scheduling",
            icon: FiHome,
            iconBg: "bg-blue-600",
            path: "roof",
        },
        {
            id: "wall",
            title: "Wall",
            description: "Wall design and estimation scheduling",
            icon: BrickWall,
            iconBg: "bg-violet-600",
            path: "wall",
        },
        {
            id: "floor",
            title: "Floor",
            description: "Floor design and estimation scheduling",
            icon: Grid2X2,
            iconBg: "bg-emerald-600",
            path: "floor",
        },
        {
            id: "timber",
            title: "Timber",
            description: "Timber job scheduling and tracking",
            icon: GiWoodBeam,
            iconBg: "bg-amber-600",
            path: "timber",
        },
    ];

    return (
        <div className="schedule-hub flex flex-col gap-8">

            {/* ── Header ───────────────────────────────────────────────────── */}
            <div>
                <BackButton
                    text="Go back"
                    variant="ghost"
                    size="sm"
                    iconType="chevron"
                    className="text-blue-600 hover:text-blue-800 mb-2"
                />
                <h1 className="text-2xl font-bold text-zinc-700">Schedule</h1>
                <p className="text-gray-500 text-sm mt-1">
                    Choose a schedule to view and manage your jobs
                </p>
            </div>

            {/* ── Primary tiles ────────────────────────────────────────────── */}
            <div>
                <SectionLabel>Primary Schedules</SectionLabel>
                <div className="schedule-primary-grid grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                    {primarySchedules.map((item) => (
                        <PrimaryCard
                            key={item.id}
                            item={item}
                            onClick={() => go(item.path)}
                            extra={
                                item.id === "delivery" ? (
                                    <a
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/${companySlug}/schedule/delivery/finished`, {
                                                state: { from: location.pathname },
                                            });
                                        }}
                                        className="flex items-center gap-1.5 self-start px-3 py-1.5 text-[11px] font-semibold bg-zinc-700 text-white rounded-lg hover:bg-zinc-800 transition-colors"
                                    >
                                        <Truck size={11} />
                                        Finished Deliveries
                                    </a>
                                ) : null
                            }
                        />
                    ))}
                </div>
            </div>

            {/* ── Department tiles ─────────────────────────────────────────── */}
            <div>
                <SectionLabel>Department Schedules</SectionLabel>
                <div className="schedule-dept-grid grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {deptSchedules.map((item) => (
                        <DeptCard
                            key={item.id}
                            item={item}
                            onClick={() => go(item.path)}
                        />
                    ))}
                </div>
            </div>

        </div>
    );
};

export default ScheduleHub;
